import type { Module } from './modules';

/**
 * UI-facing Conversation shape — NOT a direct mirror of the API contract.
 *
 * The wire shape from `apps/api` (see `apps/web/src/lib/api/endpoints/workspaces.ts`
 * `Conversation` interface) returns `title`, `titleSource`, `titleLocked`, `icon`,
 * `meta`, `position`, etc. This interface is what
 * `apps/web/src/features/conversations/lib/conversationAdapter.ts` produces via
 * `mapConversationSummary`:
 *
 *   - `name`        derived from API `title` (or `'New chat'` fallback)
 *   - `color`       adapter default (`'#3b82f6'`) — kept for UI change-detection
 *                   (e.g., `ConversationContext.tsx`) and seed-on-create sites
 *                   (e.g., `MainLayout.tsx`). Not a real API field. (#2448)
 *   - `visibility`  adapter default (`'private'`) — placeholder until ACLs land.
 *                   Not a real API field. (#2448)
 *
 * When the adapter is removed (tracked by #2448 follow-ups), components should
 * consume `Conversation` from `@/lib/api/endpoints/workspaces` directly and use
 * `title` instead of `name`.
 */
export interface Conversation {
  id: string;
  name: string;
  icon?: string;
  /** Adapter default — see interface docstring. */
  color?: string;
  /** Adapter default — see interface docstring. */
  visibility?: 'private' | 'shared' | 'public';
  archivedAt?: string | null;
  createdAt?: string;
  updatedAt?: string;
  version?: number;
  position?: number;
  meta?: {
    modules?: ConversationModulesMetaV1 | ConversationModulesMetaV2;
    privacyMode?: 'local' | 'cloud' | 'hybrid';
    menuOrder?: string[];
    pinned?: boolean;
    isPinned?: boolean;
    workspaceDefaultConversation?: boolean | number | string;
    [key: string]: any;
  };
  modules?: Module[];
  _etag?: string;
}

export type ConversationModulesMetaV1 = Module[];

export interface ConversationModulesMetaV2 {
  version: 2;
  modules: Module[];
}

export function parseConversationModules(meta: any | undefined | null): Module[] {
  const raw = meta?.modules ?? meta;
  if (!raw) return undefined as unknown as Module[];

  let modules: Module[] | undefined;
  if (Array.isArray(raw)) {
    modules = raw as Module[];
  } else if (Array.isArray(raw.modules)) {
    modules = raw.modules as Module[];
  }

  // Treat empty arrays as "no data" so callers can fall back to conversation.modules
  if (!modules || modules.length === 0) {
    return undefined as unknown as Module[];
  }
  return modules;
}

export function serializeConversationModules(modules: Module[]): ConversationModulesMetaV2 {
  return { version: 2, modules };
}

export function isConversationPinned(conversation: Pick<Conversation, 'meta'> | null | undefined): boolean {
  const pinned = conversation?.meta?.pinned;
  if (typeof pinned === 'boolean') return pinned;

  const legacyPinned = conversation?.meta?.isPinned;
  return legacyPinned === true;
}

export function isWorkspaceDefaultConversation(
  conversation: Pick<Conversation, 'meta'> | null | undefined,
): boolean {
  const marker = conversation?.meta?.workspaceDefaultConversation;
  return marker === true || marker === 1 || marker === '1';
}

export interface ConversationItem {
  id: string;
  name: string;
  icon?: string | null;
  memberCount: number;
  updatedAt?: string;
  source?: 'remote' | 'local';
}

export interface ConversationsState {
  conversations: ConversationItem[];
  activeConversationId: string | null;
  loading: boolean;
  error: string | null;
  localConversations: Record<string, LocalConversation>;
}

export interface LocalConversation {
  id: string;
  name: string;
  icon: string;
  items: string[];
  pending?: boolean;
}
